(() => {
  const CACHE_KEY = 'rubies-mmc-product-index-v3';

  class ProductProvider {
    constructor(shell, status, progress, percent, eta) {
      this.token = shell.dataset.storefrontToken || '';
      this.version = shell.dataset.apiVersion || '2026-07';
      this.cacheMinutes = Number(shell.dataset.cacheMinutes || 30);
      this.status = status;
      this.progress = progress;
      this.progressBar = progress?.querySelector('[data-mmc-page-progress-bar]');
      this.percent = percent;
      this.eta = eta;
      this.details = new Map();
      this.startedAt = performance.now();
    }

    async query(query, variables = {}) {
      const response = await fetch(`/api/${this.version}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': this.token },
        body: JSON.stringify({ query, variables })
      });
      if (!response.ok) throw new Error(`Storefront API request failed (${response.status}).`);
      const payload = await response.json();
      if (payload.errors?.length) throw new Error(payload.errors.map((error) => error.message).join(' '));
      return payload.data;
    }

    openCacheDatabase() {
      if (!('indexedDB' in window)) return Promise.resolve(null);
      if (this.cacheDatabasePromise) return this.cacheDatabasePromise;
      this.cacheDatabasePromise = new Promise((resolve) => {
        const request = indexedDB.open('rubies-mmc-cache', 1);
        request.onupgradeneeded = () => {
          const database = request.result;
          if (!database.objectStoreNames.contains('catalogues')) database.createObjectStore('catalogues');
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
        request.onblocked = () => resolve(null);
      });
      return this.cacheDatabasePromise;
    }

    async readCache() {
      const database = await this.openCacheDatabase();
      if (!database) return null;
      return new Promise((resolve) => {
        const transaction = database.transaction('catalogues', 'readonly');
        const request = transaction.objectStore('catalogues').get(CACHE_KEY);
        request.onsuccess = () => {
          const cached = request.result;
          const valid = cached?.savedAt
            && Date.now() - cached.savedAt <= this.cacheMinutes * 60000
            && Array.isArray(cached.products);
          resolve(valid ? cached.products : null);
        };
        request.onerror = () => resolve(null);
        transaction.onabort = () => resolve(null);
      });
    }

    async writeCache(products) {
      const database = await this.openCacheDatabase();
      if (!database) return;
      await new Promise((resolve) => {
        const transaction = database.transaction('catalogues', 'readwrite');
        transaction.objectStore('catalogues').put({ savedAt: Date.now(), products }, CACHE_KEY);
        transaction.oncomplete = resolve;
        transaction.onerror = resolve;
        transaction.onabort = resolve;
      });
      try { localStorage.removeItem(CACHE_KEY); } catch (_) { /* Old cache cleanup is non-fatal. */ }
    }

    updateProgress(value, message, etaSeconds = null) {
      const rounded = Math.max(0, Math.min(100, Math.round(value)));
      if (this.status && message) this.status.textContent = message;
      if (this.progress) this.progress.setAttribute('aria-valuenow', rounded);
      if (this.progressBar) this.progressBar.style.width = `${rounded}%`;
      if (this.percent) this.percent.textContent = `${rounded}%`;
      if (this.eta) {
        if (rounded >= 100) this.eta.textContent = 'Ready';
        else if (Number.isFinite(etaSeconds) && etaSeconds > 0) {
          const seconds = Math.max(1, Math.round(etaSeconds));
          this.eta.textContent = seconds < 60
            ? `About ${seconds} ${seconds === 1 ? 'second' : 'seconds'} remaining`
            : `About ${Math.ceil(seconds / 60)} minutes remaining`;
        } else this.eta.textContent = 'Calculating time remaining…';
      }
    }

    values(value) {
      if (!value) return [];
      try {
        const parsed = JSON.parse(value);
        return (Array.isArray(parsed) ? parsed : [parsed]).map(String).filter(Boolean);
      } catch (_) { return [String(value)]; }
    }

    normalise(product) {
      const variants = product.variants?.nodes || [];
      return {
        id: product.id, handle: product.handle, title: product.title, vendor: product.vendor || '',
        productType: product.productType || '', featuredImage: product.featuredImage,
        styleCode: product.styleCode?.value || variants.find((variant) => variant.sku)?.sku || '',
        skus: variants.map((variant) => variant.sku).filter(Boolean),
        gender: [...new Set(variants.flatMap((variant) => this.values(variant.gender?.value)))],
        audience: [...new Set(variants.flatMap((variant) => this.values(variant.audience?.value)))],
        variantFilters: variants.map((variant) => ({
          gender: this.values(variant.gender?.value),
          audience: this.values(variant.audience?.value)
        })),
        theme: this.values(product.theme?.value), character: this.values(product.character?.value),
        license: product.license?.reference?.title?.value || '',
        franchise: product.franchise?.reference?.title?.value || ''
      };
    }

    async loadIndex(totalProducts) {
      const cached = await this.readCache();
      if (cached) {
        this.updateProgress(90, `Loading ${cached.length} saved products…`, 1);
        return cached;
      }
      const products = [];
      let after = null;
      let more = true;
      let batch = 1;
      while (more) {
        const beforeLoad = products.length;
        const elapsedSeconds = (performance.now() - this.startedAt) / 1000;
        const estimatedRemaining = beforeLoad > 0 && totalProducts > beforeLoad
          ? (elapsedSeconds / beforeLoad) * (totalProducts - beforeLoad)
          : null;
        const progress = totalProducts > 0 ? Math.min(90, (beforeLoad / totalProducts) * 90) : Math.min(85, batch * 5);
        this.updateProgress(progress, `Preparing your product library… ${beforeLoad} of ${totalProducts || 'all'} products`, estimatedRemaining);
        const data = await this.query(this.indexQuery(), { after });
        products.push(...data.products.nodes.map((product) => this.normalise(product)));
        more = data.products.pageInfo.hasNextPage;
        after = data.products.pageInfo.endCursor;
        batch += 1;
      }
      this.updateProgress(90, `${products.length} products prepared. Organising your filters…`, 1);
      await this.writeCache(products);
      return products;
    }

    indexQuery() {
      return `query MmcIndex($after: String) {
        products(first: 100, after: $after) {
          nodes {
            id handle title vendor productType featuredImage { url altText }
            styleCode: metafield(namespace: "custom", key: "style_code") { value }
            theme: metafield(namespace: "filter", key: "theme") { value }
            character: metafield(namespace: "filter", key: "character") { value }
            license: metafield(namespace: "license", key: "license") { reference { ... on Metaobject { title: field(key: "title") { value } } } }
            franchise: metafield(namespace: "license", key: "franchise") { reference { ... on Metaobject { title: field(key: "title") { value } } } }
            variants(first: 100) { nodes { sku gender: metafield(namespace: "filter", key: "gender") { value } audience: metafield(namespace: "filter", key: "audience") { value } } }
          }
          pageInfo { hasNextPage endCursor }
        }
      }`;
    }

    createCard(product) {
      const article = document.createElement('article');
      article.className = 'mmc-card';
      Object.assign(article.dataset, {
        mmcCard: '', productId: product.id, selectionKey: product.handle, title: product.title,
        handle: product.handle, styleCode: product.styleCode, skus: product.skus.join(' '),
        license: product.license || product.vendor, filterGender: product.gender.join('||'),
        filterAudience: product.audience.join('||'), filterProductType: product.productType,
        filterTheme: product.theme.join('||'), filterCharacter: product.character.join('||'),
        filterBrand: product.vendor, filterLicense: product.license, filterFranchise: product.franchise
      });
      article.dataset.filterVariantPairs = JSON.stringify(product.variantFilters || []);

      const select = document.createElement('label');
      select.className = 'mmc-card__select';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox'; checkbox.dataset.mmcSelect = '';
      checkbox.setAttribute('aria-label', `Select ${product.title} for download`);
      const box = document.createElement('span');
      box.className = 'mmc-card__select-box'; box.setAttribute('aria-hidden', 'true');
      box.innerHTML = '<svg viewBox="0 0 16 16" width="12" height="12"><path d="m3 8 3 3 7-7"></path></svg>';
      select.append(checkbox, box);

      const button = document.createElement('button');
      button.className = 'mmc-card__button'; button.type = 'button'; button.dataset.mmcOpen = '';
      const imageWrap = document.createElement('span'); imageWrap.className = 'mmc-card__image-wrap';
      const badge = document.createElement('span'); badge.className = 'mmc-card__asset-badge'; badge.textContent = 'Product images'; imageWrap.append(badge);
      if (product.featuredImage?.url) {
        const image = document.createElement('img'); image.className = 'mmc-card__image';
        image.src = this.imageUrl(product.featuredImage.url, 700); image.alt = product.featuredImage.altText || product.title; image.loading = 'lazy';
        imageWrap.append(image);
      }
      const imageCount = document.createElement('span'); imageCount.className = 'mmc-card__count'; imageCount.textContent = 'Product images'; imageWrap.append(imageCount);
      const content = document.createElement('span'); content.className = 'mmc-card__content';
      const license = document.createElement('span'); license.className = 'mmc-card__license'; license.textContent = product.license || product.vendor;
      const title = document.createElement('span'); title.className = 'mmc-card__title'; title.textContent = product.title; content.append(license, title);
      if (product.styleCode) { const style = document.createElement('span'); style.className = 'mmc-card__sku'; style.textContent = `Style: ${product.styleCode}`; content.append(style); }
      const action = document.createElement('span'); action.className = 'mmc-card__asset-action';
      action.innerHTML = '<span class="mmc-card__asset-action-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6.5h6l2 2h10v10.5H3z"></path><path d="M12 11v5m0 0-2-2m2 2 2-2"></path></svg></span><span>View and download images</span>';
      button.append(imageWrap, content, action);

      const template = document.createElement('template'); template.dataset.mmcProductData = '';
      const data = document.createElement('div'); data.dataset.productTitle = product.title; data.dataset.productCode = product.styleCode; data.dataset.productLicense = product.license || product.vendor;
      template.content.append(data); article.append(select, button, template); return article;
    }

    async hydrateCard(card) {
      if (card.querySelector('[data-mmc-image]')) return;
      const id = card.dataset.productId;
      let product = this.details.get(id);
      if (!product) {
        const result = await this.query(`query MmcImages($id: ID!) { node(id: $id) { ... on Product { handle title images(first: 250) { nodes { url altText } } } } }`, { id });
        product = result.node;
        if (!product) throw new Error('Product images could not be loaded.');
        this.details.set(id, product);
      }
      this.applyImages(card, product);
    }

    async hydrateCards(cards, onProgress = () => {}) {
      const missingIds = [...new Set(cards.filter((card) => !card.querySelector('[data-mmc-image]')).map((card) => card.dataset.productId).filter(Boolean))];
      for (let offset = 0; offset < missingIds.length; offset += 25) {
        const ids = missingIds.slice(offset, offset + 25);
        onProgress(Math.min(offset + ids.length, missingIds.length), missingIds.length);
        const result = await this.query(`query MmcProductImages($ids: [ID!]!) { nodes(ids: $ids) { ... on Product { id handle title images(first: 250) { nodes { url altText } } } } }`, { ids });
        result.nodes.filter(Boolean).forEach((product) => this.details.set(product.id, product));
      }
      cards.forEach((card) => {
        if (!card.querySelector('[data-mmc-image]')) {
          const product = this.details.get(card.dataset.productId);
          if (product) this.applyImages(card, product);
        }
      });
    }

    applyImages(card, product) {
      const data = card.querySelector('[data-mmc-product-data]')?.content.firstElementChild;
      if (!data || data.querySelector('[data-mmc-image]')) return;
      product.images.nodes.forEach((image, index) => {
        const item = document.createElement('div');
        const extension = image.url.split('?')[0].split('.').pop().toLowerCase();
        Object.assign(item.dataset, {
          mmcImage: '', preview: this.imageUrl(image.url, 1200), download: image.url,
          alt: image.altText || product.title,
          filename: `${this.safe(data.dataset.productCode || product.handle)}_${String(index + 1).padStart(2, '0')}.${['jpg','jpeg','png','webp','gif'].includes(extension) ? extension : 'jpg'}`
        });
        data.append(item);
      });
      const count = card.querySelector('.mmc-card__count');
      if (count) count.textContent = `${product.images.nodes.length} ${product.images.nodes.length === 1 ? 'image' : 'images'}`;
    }

    imageUrl(url, width) { return `${url}${url.includes('?') ? '&' : '?'}width=${width}`; }
    safe(value) { return String(value || 'product').replace(/[^a-z0-9._-]+/gi, '_').replace(/^_+|_+$/g, '') || 'product'; }
  }

  const fetchShell = async () => {
    const url = new URL('/collections/all', location.origin); url.searchParams.set('view', 'multimedia-centre');
    const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(`Multimedia Centre shell failed (${response.status}).`);
    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const centre = page.querySelector('[data-mmc]');
    if (!centre) throw new Error('The Multimedia Centre collection template could not be found.');
    return centre;
  };

  const initialise = async (shell) => {
    if (shell.dataset.mmcPageInitialised === 'true') return;
    shell.dataset.mmcPageInitialised = 'true';
    const mount = shell.querySelector('[data-mmc-page-mount]');
    const error = shell.querySelector('[data-mmc-page-error]');
    const status = shell.querySelector('[data-mmc-page-loading-status]');
    const progress = shell.querySelector('[data-mmc-page-progress]');
    const percent = shell.querySelector('[data-mmc-page-progress-percent]');
    const eta = shell.querySelector('[data-mmc-page-progress-eta]');
    if (!mount) return;
    if (error) error.hidden = true;
    try {
      if (!shell.dataset.storefrontToken) throw new Error('Add the public Storefront API token in the Product Multimedia Centre page settings.');
      const provider = new ProductProvider(shell, status, progress, percent, eta);
      provider.updateProgress(2, 'Checking your product library…');
      const centre = await fetchShell();
      const totalProducts = Number(centre.querySelector('[data-mmc-count]')?.dataset.totalProducts || 0);
      provider.updateProgress(5, 'Preparing your product library…');
      const products = await provider.loadIndex(totalProducts);
      provider.updateProgress(94, `Organising filters for ${products.length} products…`, 1);
      const grid = centre.querySelector('[data-mmc-grid]');
      const pagination = centre.querySelector('[data-mmc-pagination]');
      const count = centre.querySelector('[data-mmc-count]');
      grid.replaceChildren(...products.map((product) => provider.createCard(product)));
      pagination?.replaceChildren();
      if (count) { count.dataset.totalProducts = products.length; }
      centre.mmcProductProvider = provider;
      provider.updateProgress(100, 'Your Multimedia Centre is ready.', 0);
      mount.replaceChildren(centre);
      window.MMCInitialise?.(mount);
    } catch (loadError) {
      console.error('[Multimedia Centre page]', loadError); mount.hidden = true;
      if (error) { error.hidden = false; const message = error.querySelector('p'); if (message) message.textContent = loadError.message; }
    }
  };

  const initialiseAll = (scope = document) => scope.querySelectorAll('[data-mmc-page-shell]').forEach(initialise);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialiseAll()); else initialiseAll();
  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
